import type { Contracts } from "@/src/gql/graphql";
import type { DetailAction } from "@/components/shared/UniversalDetailPage/types/detailPage";
import {
  contractDetailTabs,
  contractDetailDefaultTab,
} from "@/features/contracts/config/contract-detail-tabs";

/**
 * Strongly-typed detail/row actions for contracts. Contracts are read-mostly
 * (created by the e-signature send flow), so the action set is View + admin
 * Delete. Actions that launch a URL (View document, Open signing link) use the
 * `href` launcher rather than an ActionHandlerName.
 */
export const contractDetailActions: DetailAction<Contracts>[] = [
  {
    id: "view-details",
    label: "View Details",
    icon: "eye",
    variant: "outline",
    primary: true,
    action: "viewDetails",
  },
  {
    // Straight to the raw agreement — the question reps most often open a
    // contract row to answer. Deep-links the detail page's Document tab rather
    // than the provider's signing link (which is the CUSTOMER's page) or the
    // signed-PDF link (which expires after ~3 days).
    id: "view-document",
    label: "View document",
    icon: "file-text",
    variant: "outline",
    primary: true,
    href: (item: Contracts) => `/contracts/${item.id}?tab=document`,
    tooltip: "Open the raw agreement — the executed PDF, or the draft as sent",
  },
  {
    id: "edit",
    label: "Edit",
    icon: "edit",
    variant: "outline",
    primary: false,
    action: "editContract",
    tooltip:
      "Edit the Hub filing — company/contact/deal linkage, document name, notes. The signed document can't be changed.",
  },
  {
    id: "resend",
    label: "Resend signature request",
    icon: "refresh-cw",
    variant: "outline",
    primary: false,
    action: "resendContract",
    tooltip: "Resend the e-signature request to every signer",
  },
  {
    id: "renew",
    label: "Renew contract",
    icon: "refresh-cw",
    variant: "outline",
    primary: false,
    action: "renewContract",
    tooltip: "Start a fresh contract for this client, pre-filled from this one",
  },
  {
    id: "duplicate",
    label: "Duplicate contract",
    icon: "copy",
    variant: "outline",
    primary: false,
    action: "duplicateContract",
    tooltip: "Start a new contract pre-filled from this one",
  },
  {
    id: "export",
    label: "Export signed PDF",
    icon: "download",
    variant: "outline",
    primary: false,
    action: "exportContract",
    visible: (item: Contracts) => item.status === "signed",
    tooltip: "Download the executed PDF (available once the contract is signed)",
  },
  {
    id: "withdraw",
    label: "Withdraw / Void contract",
    icon: "x",
    variant: "destructive",
    primary: false,
    action: "withdrawContract",
    allowedRoles: ["wtc_admin"],
    tooltip:
      "Void the e-signature request (admin only). No undo — to start over, re-send a new contract.",
  },
  {
    id: "terminate",
    label: "Terminate contract",
    icon: "x",
    variant: "destructive",
    primary: false,
    action: "terminateContract",
    allowedRoles: ["wtc_admin"],
    tooltip:
      "Terminate (same as Withdraw) — stops signing and marks the contract voided (admin only).",
  },
  {
    id: "delete",
    label: "Delete",
    icon: "trash2",
    variant: "destructive",
    primary: false,
    action: "deleteItem",
    allowedRoles: ["wtc_admin"],
    tooltip: "Delete this contract record (admin only)",
  },
];

/**
 * Detail page configuration for contracts. Loosely typed (cast where consumed,
 * mirroring RecurringChargeDetailConfig) so URL-launch actions can declare an
 * `href` derived from the contract's signing/signed links.
 */
export const CONTRACT_DETAIL_CONFIG = {
  entity: "contracts",
  displayName: "Contract",

  titleTemplate: (item: Contracts) =>
    item.document_name || item.template_name || "Contract",

  // Sales-oriented modal tabs (Overview / Company / Contact / Deal /
  // Communications). Definitions live in config/contract-detail-tabs.ts and are
  // rendered by useContractDetailModalTabs.
  tabs: contractDetailTabs,
  defaultTab: contractDetailDefaultTab,

  fields: {
    document_name: { type: "text", label: "Document", editable: false },
    template_name: { type: "text", label: "Template", editable: false },
    status: {
      type: "select",
      label: "Status",
      editable: false,
      options: [
        { label: "Draft", value: "draft" },
        { label: "Sent", value: "sent" },
        { label: "Viewed", value: "viewed" },
        { label: "Partially Signed", value: "partially_signed" },
        { label: "Signed", value: "signed" },
        { label: "Declined", value: "declined" },
        { label: "Withdrawn", value: "withdrawn" },
        { label: "Error", value: "error" },
      ],
    },
    delivery_mode: { type: "select", label: "Delivery", editable: false },
    company_id: { type: "company-select", label: "Company", editable: false },
    contact_id: { type: "client-select", label: "Contact", editable: false },
    recipient_name: { type: "text", label: "Recipient", editable: false },
    recipient_email: { type: "text", label: "Recipient Email", editable: false },
    sent_at: { type: "date", label: "Sent", editable: false },
    signed_at: { type: "date", label: "Signed At", editable: false },
    last_event_at: { type: "date", label: "Last Event", editable: false },
    signing_url: { type: "text", label: "Signing Link", editable: false },
    signed_pdf_url: { type: "text", label: "Signed PDF", editable: false },
  },

  actions: [
    {
      id: "open-signing-link",
      label: "Open signing link",
      icon: "link",
      variant: "outline" as const,
      // Secondary to "View document": this is the CUSTOMER's signing page, for
      // copying/re-sending — not the way a rep should read the agreement.
      primary: false,
      href: (item: Contracts) => item.signing_url || "",
      visible: (item: Contracts) =>
        Boolean(item.signing_url) && item.status !== "signed",
      tooltip: "The customer's e-signature signing page — to copy or re-share",
    },
    {
      // The raw agreement, always available — executed PDF once signed, the
      // draft as sent before that. Points at our own Document tab rather than
      // `signed_pdf_url`, which is an eSignatures link that expires ~3 days
      // after signing (so the old button was dead on any older contract).
      id: "view-document",
      label: "View document",
      icon: "file-text",
      variant: "outline" as const,
      primary: true,
      href: (item: Contracts) => `/contracts/${item.id}?tab=document`,
      tooltip: "Open the raw agreement — the executed PDF, or the draft as sent",
    },
    {
      id: "edit",
      label: "Edit",
      icon: "edit",
      variant: "outline" as const,
      primary: false,
      action: "editContract",
      tooltip:
        "Edit the Hub filing — company/contact/deal linkage, document name, notes. The signed document can't be changed.",
    },
    {
      id: "resend",
      label: "Resend signature request",
      icon: "refresh-cw",
      variant: "outline" as const,
      primary: false,
      action: "resendContract",
      tooltip: "Resend the e-signature request to every signer",
    },
    {
      id: "renew",
      label: "Renew contract",
      icon: "refresh-cw",
      variant: "outline" as const,
      primary: false,
      action: "renewContract",
      tooltip: "Start a fresh contract for this client, pre-filled from this one",
    },
    {
      id: "duplicate",
      label: "Duplicate contract",
      icon: "copy",
      variant: "outline" as const,
      primary: false,
      action: "duplicateContract",
      tooltip: "Start a new contract pre-filled from this one",
    },
    {
      id: "export",
      label: "Export signed PDF",
      icon: "download",
      variant: "outline" as const,
      primary: false,
      action: "exportContract",
      tooltip: "Open the signed PDF in a new tab (available once the contract is signed)",
    },
    {
      id: "withdraw",
      label: "Withdraw / Void contract",
      icon: "x",
      variant: "destructive" as const,
      primary: false,
      action: "withdrawContract",
      allowedRoles: ["wtc_admin"],
      tooltip:
        "Void the e-signature request (admin only). No undo — to start over, re-send a new contract.",
    },
    {
      id: "terminate",
      label: "Terminate contract",
      icon: "x",
      variant: "destructive" as const,
      primary: false,
      action: "terminateContract",
      allowedRoles: ["wtc_admin"],
      tooltip:
        "Terminate (same as Withdraw) — stops signing and marks the contract voided (admin only).",
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash2",
      variant: "destructive" as const,
      primary: false,
      action: "delete",
      allowedRoles: ["wtc_admin"],
      tooltip: "Delete this contract record (admin only)",
    },
  ],
};
